import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useTabThumb } from '../../../hooks/useTabThumb'
import { longDate } from '../../../lib/dates'
import { DashboardPageHeader } from '../../DashboardPageHeader'
import { PlanComparisonTable, type PlanComparisonSection } from '../../DashboardSections/PlanComparisonTable'
import { cachedBillingPlan, cancelCheckout, cancelMembership, refreshBilling, serverErrorCode, startCheckout, type BillingPlan } from '../../../lib/api'
import { ConfirmDialog } from '../ConfirmDialog'
import { CancelMembershipSummary } from '../../CancelMembershipSummary'
import { EmbeddedCheckoutPanel, type CheckoutSummary } from './EmbeddedCheckout'
import { MembershipExistsDialog } from './MembershipExistsDialog'
import { CheckoutSuccess } from './CheckoutSuccess'
import { ELITE_PROJECT_LIMIT, formatPlanLimit, PLATFORM_FEE_PERCENT, PROJECT_LIMITS, SITE_PLAN_LIMITS } from '../../../lib/planLimits'
/* Las ventajas de cada plan viven aparte: las lee también el resumen de lo que
   se pierde al cancelar, y escritas aquí había que reescribirlas allá. */
import { COURSE_LIMITS, blogLine, courseLimitLabel, businessAppLimits, type PlanFeature } from '../../../lib/planFeatures'
import { useI18n } from '../../../lib/i18n'
import { BETA_DISCOUNT, BETA_MONTHS, BETA_OFF_PERCENT, PLAN_PRICES, formatUsd } from '../../../lib/planPricing'
import type { Translate } from '@webbuilder/i18n'
import { errorMessage } from './offerShared'
import { IconArrowRight, IconCheck, IconLoader2, IconLock, IconPlus, IconRefresh, IconShieldCheck, IconX } from '@tabler/icons-react'
import { MascotOutlinePattern } from '../../MascotOutlinePattern'

type BillingCycle = 'annual' | 'monthly'

/** Map the plan name to the API plan id (only paid plans are purchasable). */
/** La fecha hasta la que sigue vigente lo pagado, como se lee en una frase. */
function untilLabel(iso: string | null, t: Translate): string {
  if (!iso) return t('plans.untilPeriodEnd')
  return longDate(iso)
}

const PLAN_ID: Record<string, 'pro' | 'business' | undefined> = {
  Pro: 'pro',
  Business: 'business',
}

/** API plan id → the plan card it corresponds to. */
const PLAN_NAME: Record<BillingPlan, string> = {
  free: 'Starter',
  pro: 'Pro',
  business: 'Business',
}

/**
 * Una ventaja del plan.
 *
 * `annualOnly` es lo que separa los dos ciclos: el dominio gratuito lo paga
 * véltica el día uno y sólo se sostiene si el cliente ya dejó doce meses por
 * delante, así que en mensual la ventaja sigue en la lista pero tachada. Se
 * queda a la vista en vez de desaparecer porque enseñar lo que se pierde al
 * pagar mes a mes es justo el argumento del plan anual.
 */




type SitePlan = {
  name: string
  eyebrow: string
  annualPrice: number | null
  monthlyPrice: number | null
  description: string
  /** Una por fila de `cardFeatureRows`, en el mismo orden en las cuatro. */
  features: CardFeature[]
  cta: string
  comingSoon?: boolean
}

/* Los precios y la beta viven en `lib/planPricing`: los lee también el aviso
   de tope, que prometía «desde $9.50» cuando Pro ya costaba otra cosa. */

/**
 * Cuánto ahorra el plan anual con beta frente a pagar mes a mes, en un solo
 * número para toda la página.
 *
 * Los descuentos no se suman, se encadenan: el anual ya es ~20% menos que el
 * mensual y la beta quita un 40% sobre ESE precio, así que se paga un 80% × 60%
 * = 48% del mensual. Con los precios reales sale 51% en Pro, 52% en Business y
 * 52.5% en Elite.
 *
 * Se toma el MENOR de todos y se redondea hacia abajo al múltiplo de cinco: así
 * la página dice «Ahorra 50%» en todos los planes, que es la cifra redonda de la
 * beta, y nunca promete de más en ninguno. Sale de los precios y no se escribe a
 * mano, para que al acabar la beta (`BETA_DISCOUNT` a 1) vuelva sola al ahorro
 * del anual sin descuento.
 */
function annualSavingPercent(plans: SitePlan[]): number {
  const savings = plans
    .filter((plan) => (plan.monthlyPrice ?? 0) > 0 && (plan.annualPrice ?? 0) > 0)
    .map((plan) => 1 - ((plan.annualPrice ?? 0) * BETA_DISCOUNT) / (plan.monthlyPrice ?? 1))
  if (savings.length === 0) return 0
  return Math.floor((Math.min(...savings) * 100) / 5) * 5
}


/** Muestra enteros sin decimales y medios con 2 (8, 6.50, 19.50). */
/**
 * Un importe en dólares, con separador de miles: «1,188» se lee de un vistazo
 * y «1188» parece un código. Sin decimales cuando es entero.
 */
function formatPrice(n: number): string {
  return formatUsd(n)
}

function formatContactLimitValue(limit: number | null, t: Translate): string {
  return limit === null ? t('plans.limit.unlimitedM') : formatPlanLimit(limit)
}


/**
 * Lo mismo para Elite.
 *
 * ── Dónde va «ilimitado» y dónde va un número ───────────────────────────────
 *
 * La regla es una y se explica en una línea: **ilimitado donde no cuesta, un
 * número donde cuesta**.
 *
 *   · Una oferta, un servicio reservable o una colección son FILAS en una base
 *     de datos. Guardar la número diez mil cuesta lo mismo que la primera: nada
 *     medible. Ahí «ilimitado» es verdad y además es el argumento del plan.
 *   · Un envío de formulario dispara un correo, y un correo se paga por unidad
 *     a un proveedor. «Ilimitados» ahí no era generosidad: era una factura
 *     abierta contra un precio cerrado, y el único de los cuatro planes que
 *     perdía dinero por ella.
 *
 * Los 7.500 envíos son doscientos cincuenta AL DÍA. Un sitio que recibe
 * doscientos cincuenta mensajes diarios por formulario no es un sitio que se topa con un cupo: es
 * uno con el que hay que hablar. Se siente ilimitado sin serlo, que es
 * exactamente lo que tiene que hacer el tope de un plan tope.
 */
function eliteAppLimits(t: Translate) {
  return {
    formSubmissions: t('plans.limit.submissions', { count: formatPlanLimit(7_500) }),
    marketingEmails: t('plans.limit.soon'),
    offers: t('plans.limit.unlimitedF'),
    teamMembers: '15',
    courses: courseLimitLabel(COURSE_LIMITS.creator, t),
  } as const
}

/**
 * Los límites de sitio de Elite, escritos a mano.
 *
 * `SITE_PLAN_LIMITS` está tecleado por `BillingPlan`, que es el enum que
 * comparten el panel, el worker y la base de datos, y ahí Elite todavía no
 * existe. Añadirlo sólo para pintar una tarjeta obligaría a tocar el enum de
 * facturación entero; mientras el plan siga en «Próximamente», estas cifras son
 * un texto de venta y viven como tal.
 */
const ELITE_SITE_LIMITS = { collections: 500, cmsItems: 50_000 } as const

/**
 * Las cuatro tarjetas.
 *
 * Función y no constante por lo mismo que las ventajas: hay texto dentro, y una
 * constante de módulo no puede llamar a un hook. El NOMBRE del plan no pasa por
 * el catálogo —«Pro» se llama Pro en los dos idiomas, y es además la clave con
 * la que se casa `PLAN_ID`—; lo que se traduce es lo que se lee alrededor.
 */
/**
 * Lo que dice cada tarjeta, en FILAS y no en cuatro listas sueltas.
 *
 * Eran cuatro listas con cosas distintas en órdenes distintos —«Todo lo de
 * Pro, más:» y lo que tocara—, y comparar dos planes obligaba a buscar la
 * misma ventaja en alturas diferentes. Ahora las cuatro recorren las mismas
 * siete filas en el mismo orden, y la cuadrícula las alinea a la misma
 * altura: se compara leyendo de lado a lado, como en una tabla.
 *
 * Siete y no todo: son las que deciden la compra —cuánto sitio, cuánto se
 * vende y con qué comisión, dominio propio, las apps de negocio y los sitios
 * que se generan con IA—. El detalle completo está en la comparativa de abajo,
 * y repetirlo aquí convertía cada tarjeta en una tabla.
 *
 * Lo que un plan no incluye se queda en su fila, con una ✗ y en gris claro.
 * Esconderlo rompería la alineación, y verlo apagado es precisamente lo que
 * dice qué se gana al subir.
 *
 * El orden va de lo que se elige primero a lo que se mira al final: la
 * capacidad del sitio, lo de vender, el dominio, las apps y la IA.
 *
 * Las cifras salen de los mismos topes que aplica el servidor y que pinta la
 * tabla comparativa, nunca escritas aquí.
 */
type CardFeature = PlanFeature & { excluded?: boolean }

function cardFeatureRows(t: Translate): CardFeature[][] {
  const business = businessAppLimits(t)
  const elite = eliteAppLimits(t)
  const on = (label: string, annualOnly = false): CardFeature => (annualOnly ? { label, annualOnly: true } : { label })
  const off = (label: string): CardFeature => ({ label, excluded: true })
  const projects = (count: number) => t('plans.feat.projects', { count: formatPlanLimit(count) })
  const offers = (limit: number | null) =>
    limit === null ? t('plans.feat.offersUnlimited') : t('plans.limit.offers', { count: formatPlanLimit(limit) })
  const fee = (percent: number) => t('plans.feat.fee', { fee: percent })
  /* Las mismas cifras que la fila de la tabla comparativa, que a su vez son
     las de `USAGE_LIMITS` en el worker. */
  const generations = (amount: string) => t('plans.feat.aiGenerations', { amount })
  const free = SITE_PLAN_LIMITS.free
  const pro = SITE_PLAN_LIMITS.pro
  const biz = SITE_PLAN_LIMITS.business
  //          Starter                                  Pro                                           Business                                          Elite
  return [
    [on(projects(PROJECT_LIMITS.free ?? 0)), on(projects(PROJECT_LIMITS.pro ?? 0)), on(projects(PROJECT_LIMITS.business ?? 0)), on(projects(ELITE_PROJECT_LIMIT))],
    [on(t('plans.feat.pages', { count: formatPlanLimit(free.pages) })), on(t('plans.feat.pages', { count: formatPlanLimit(pro.pages) })), on(t('plans.feat.unlimitedPages')), on(t('plans.feat.unlimitedPages'))],
    [on(offers(free.offers)), on(offers(pro.offers)), on(offers(biz.offers)), on(offers(null))],
    [on(blogLine(free, t)), on(blogLine(pro, t)), on(blogLine(biz, t)), on(blogLine({ blogPosts: null }, t))],
    [on(fee(PLATFORM_FEE_PERCENT.free)), on(fee(PLATFORM_FEE_PERCENT.pro)), on(fee(PLATFORM_FEE_PERCENT.business)), on(fee(PLATFORM_FEE_PERCENT.business))],
    [off(t('plans.feat.customDomain')), on(t('plans.feat.customDomain')), on(t('plans.feat.customDomain')), on(t('plans.feat.customDomain'))],
    /* Los cursos ocupan el lugar de «Inbox y marketing», que tampoco sale en la
       beta: sigue en la tabla comparativa de abajo. */
    [off(t('plans.feat.coursesOff')), on(t('plans.feat.courses', { limit: courseLimitLabel(COURSE_LIMITS.pro, t) })), on(t('plans.feat.courses', { limit: business.courses })), on(t('plans.feat.courses', { limit: elite.courses }))],
    [on(generations(t('plans.table.val.generationsTotal', { count: formatPlanLimit(3) }))), on(generations(t('plans.table.val.generationsMonthly', { count: formatPlanLimit(50) }))), on(generations(t('plans.table.val.generationsMonthly', { count: formatPlanLimit(200) }))), on(generations(t('plans.table.val.generationsMonthly', { count: formatPlanLimit(500) })))],
  ]
}

/**
 * Las cuatro tarjetas.
 *
 * Función y no constante por lo mismo que las ventajas: hay texto dentro, y una
 * constante de módulo no puede llamar a un hook. El NOMBRE del plan no pasa por
 * el catálogo —«Pro» se llama Pro en los dos idiomas, y es además la clave con
 * la que se casa `PLAN_ID`—; lo que se traduce es lo que se lee alrededor.
 */
function sitePlans(t: Translate): SitePlan[] {
  const rows = cardFeatureRows(t)
  const column = (index: number) => rows.map((row) => row[index] as CardFeature)
  return [
    {
      name: 'Starter',
      eyebrow: t('plans.card.starter.eyebrow'),
      annualPrice: 0,
      monthlyPrice: 0,
      description: t('plans.card.starter.body'),
      features: column(0),
      cta: t('plans.card.starter.cta'),
    },
    {
      name: 'Pro',
      eyebrow: t('plans.card.pro.eyebrow'),
      annualPrice: PLAN_PRICES.pro.annual,
      monthlyPrice: PLAN_PRICES.pro.monthly,
      description: t('plans.card.pro.body'),
      features: column(1),
      cta: t('plans.card.pro.cta'),
    },
    {
      name: 'Business',
      eyebrow: t('plans.card.business.eyebrow'),
      annualPrice: PLAN_PRICES.business.annual,
      monthlyPrice: PLAN_PRICES.business.monthly,
      description: t('plans.card.business.body'),
      features: column(2),
      cta: t('plans.soon'),
      comingSoon: true,
    },
    {
      name: 'Creator',
      eyebrow: t('plans.card.elite.eyebrow'),
      annualPrice: PLAN_PRICES.elite.annual,
      monthlyPrice: PLAN_PRICES.elite.monthly,
      description: t('plans.card.elite.body'),
      features: column(3),
      cta: t('plans.soon'),
      comingSoon: true,
    },
  ]
}

/** «3 ofertas» / «Ilimitadas», para las celdas de la tabla. */
function offerCount(limit: number | null, t: Translate): string {
  return limit === null ? t('plans.limit.unlimitedF') : t('plans.limit.offers', { count: formatPlanLimit(limit) })
}

function siteComparison(t: Translate): PlanComparisonSection[] {
  const business = businessAppLimits(t)
  const elite = eliteAppLimits(t)
  const unlimitedF = t('plans.limit.unlimitedF')
  const noLimit = t('plans.table.val.noLimit')
  const withAnnual = t('plans.table.val.withAnnual')
  return [
    {
      title: t('plans.table.sec.site'),
      rows: [
        { feature: t('plans.table.row.projects'), values: [formatPlanLimit(PROJECT_LIMITS.free), formatPlanLimit(PROJECT_LIMITS.pro), formatPlanLimit(PROJECT_LIMITS.business), formatPlanLimit(ELITE_PROJECT_LIMIT)] },
        { feature: t('plans.table.row.pages'), values: [formatPlanLimit(SITE_PLAN_LIMITS.free.pages), formatPlanLimit(SITE_PLAN_LIMITS.pro.pages), unlimitedF, unlimitedF] },
        { feature: t('plans.table.row.subdomain'), values: [true, true, true, true] },
        { feature: t('plans.table.row.customDomain'), values: [false, true, true, true] },
        { feature: t('plans.table.row.freeDomain'), values: [false, withAnnual, withAnnual, withAnnual] },
        { feature: t('plans.table.row.noBadge'), values: [false, true, true, true] },
        { feature: t('plans.table.row.ssl'), values: [true, true, true, true] },
      ],
    },
    {
      title: t('plans.table.sec.cms'),
      rows: [
        { feature: t('plans.table.row.collections'), values: [formatPlanLimit(SITE_PLAN_LIMITS.free.collections), formatPlanLimit(SITE_PLAN_LIMITS.pro.collections), formatPlanLimit(SITE_PLAN_LIMITS.business.collections), formatPlanLimit(ELITE_SITE_LIMITS.collections)] },
        { feature: t('plans.table.row.cmsItems'), values: [formatPlanLimit(SITE_PLAN_LIMITS.free.cmsItems), formatPlanLimit(SITE_PLAN_LIMITS.pro.cmsItems), formatPlanLimit(SITE_PLAN_LIMITS.business.cmsItems), formatPlanLimit(ELITE_SITE_LIMITS.cmsItems)] },
        { feature: t('plans.table.row.contacts'), values: [formatContactLimitValue(SITE_PLAN_LIMITS.free.contacts, t), formatContactLimitValue(SITE_PLAN_LIMITS.pro.contacts, t), formatContactLimitValue(SITE_PLAN_LIMITS.business.contacts, t), t('plans.limit.unlimitedM')] },
        { feature: t('plans.table.row.blog'), values: [formatPlanLimit(SITE_PLAN_LIMITS.free.blogPosts), formatPlanLimit(SITE_PLAN_LIMITS.pro.blogPosts), SITE_PLAN_LIMITS.business.blogPosts === null ? unlimitedF : formatPlanLimit(SITE_PLAN_LIMITS.business.blogPosts), unlimitedF] },
        /* En todos: el buscador del menú se publica con cualquier sitio. */
        { feature: t('plans.table.row.search'), values: [true, true, true, true] },
      ],
    },
    {
      title: t('plans.table.sec.capacity'),
      // Límites reales por plan; fuente de verdad: USAGE_LIMITS en apps/worker/src/services/usage.ts.
      // Elite todavía no existe allí: su columna es propuesta comercial.
      rows: [
        /*
         * El tráfico ya no se vende como cupo.
         *
         * Estaba anunciado en cuatro tramos —10 GB, 100 GB, 1 TB, 2 TB— y no se
         * medía en ninguna parte: `bandwidth_bytes` nunca se escribe, y lo que
         * veía el cliente era una estimación. Un tope anunciado que nadie aplica
         * es una promesa que se rompe el día que alguien la pone a prueba.
         *
         * Y no hace falta aplicarlo: la salida de datos de R2 no se cobra, así que
         * el tráfico de un sitio no nos cuesta lo que le costaría a quien sirve
         * desde AWS. Es la misma razón por la que Cloudflare Pages lo anuncia
         * ilimitado. Lo que queda es una cláusula de uso razonable en los términos
         * —ver `pages/Legal.tsx`— y vigilancia interna del abuso, que es lo que
         * hacen los constructores de sitios desde hace años.
         *
         * Comercialmente además resta: «10 GB» no le dice a nadie cuántas visitas
         * son, y quien echa la cuenta con un sitio lleno de fotos se asusta.
         *
         * El almacenamiento SÍ se queda con número: ése se mide de verdad
         * (`getStorageBytes`), se bloquea al llegar al tope y nos cuesta dinero.
         */
        { feature: t('plans.table.row.visits'), values: [noLimit, noLimit, noLimit, noLimit] },
        /* Las unidades de datos no se traducen: «GB» se escribe igual en los dos
           idiomas y es un símbolo, no una palabra. */
        { feature: t('plans.table.row.storage'), values: ['1 GB', '10 GB', '100 GB', '250 GB'] },
        { feature: t('plans.table.row.ai'), values: [t('plans.table.val.standard'), t('plans.table.val.aiTimes', { times: 5 }), t('plans.table.val.aiTimes', { times: 10 }), t('plans.table.val.aiTimes', { times: 20 })] },
        /* Las generaciones de sitio completo, aparte del chat: son lo que más
           cuesta por uso. En el gratis son en total, no al mes — ver
           `USAGE_LIMITS` en el worker, que es quien las aplica. */
        { feature: t('plans.table.row.aiGenerations'), values: [t('plans.table.val.generationsTotal', { count: formatPlanLimit(3) }), t('plans.table.val.generationsMonthly', { count: formatPlanLimit(50) }), t('plans.table.val.generationsMonthly', { count: formatPlanLimit(200) }), t('plans.table.val.generationsMonthly', { count: formatPlanLimit(500) })] },
      ],
    },
    {
      title: t('plans.table.sec.apps'),
      // Business abre las apps con cupo y Elite las abre del todo: es la única
      // diferencia real entre los dos planes, así que aquí es donde se decide la
      // compra. Los cupos salen de businessAppLimits / eliteAppLimits para
      // que la tabla y las tarjetas no puedan contradecirse.
      rows: [
        { feature: t('plans.table.row.forms'), values: [t('plans.limit.submissions', { count: formatPlanLimit(25) }), t('plans.limit.submissions', { count: formatPlanLimit(1_000) }), business.formSubmissions, elite.formSubmissions] },
        { feature: t('plans.table.row.inbox'), values: [false, false, t('plans.limit.soon'), t('plans.limit.soon')] },
        { feature: t('plans.table.row.marketing'), values: [false, false, business.marketingEmails, elite.marketingEmails] },
        /* La agenda no tiene cupo propio: sus servicios cuentan como ofertas
           publicadas, en la fila de abajo. Aquí sólo si entra o no. */
        { feature: t('plans.table.row.bookings'), values: [true, true, true, true] },
        /* Venden todos los planes (ver `CAN_CONNECT_STRIPE`): lo que cambia es
           cuántas ofertas se publican y cuánta comisión se deja, que es la
           fila de abajo. Decía que Starter y Pro no vendían, y Pro vendía. */
        { feature: t('plans.table.row.sales'), values: [
          offerCount(SITE_PLAN_LIMITS.free.offers, t),
          offerCount(SITE_PLAN_LIMITS.pro.offers, t),
          business.offers,
          elite.offers,
        ] },
        /* La comisión sobre lo que vende el cliente, de la misma constante con la
           que el worker la cobra. Elite todavía no existe: va como Business. */
        { feature: t('plans.table.row.fee'), values: [
          t('plans.table.val.feePercent', { percent: PLATFORM_FEE_PERCENT.free }),
          t('plans.table.val.feePercent', { percent: PLATFORM_FEE_PERCENT.pro }),
          t('plans.table.val.feePercent', { percent: PLATFORM_FEE_PERCENT.business }),
          t('plans.table.val.feePercent', { percent: PLATFORM_FEE_PERCENT.business }),
        ] },
        /* Desde Pro: el video es lo único de la plataforma cuyo costo crece con
           el público del cliente y no con lo que guarda, así que no entra en el
           gratis. */
        { feature: t('plans.table.row.courses'), values: [false, courseLimitLabel(COURSE_LIMITS.pro, t), business.courses, elite.courses] },
        /* Pro invita desde el tope que aplica el servidor (`PLAN_LIMITS`): la
           tabla decía que no, y el servidor dejaba entrar a tres. */
        { feature: t('plans.table.row.team'), values: [false, formatPlanLimit(SITE_PLAN_LIMITS.pro.teamMembers), business.teamMembers, elite.teamMembers] },
        { feature: t('plans.table.row.roles'), values: [false, false, false, true] },
      ],
    },
    {
      title: t('plans.table.sec.support'),
      rows: [
        /* El gratuito no tiene analíticas —las ve bloqueadas—, así que es ✗ y no
           «básicas»: decía básicas y no enseñaba ninguna. */
        { feature: t('plans.table.row.analytics'), values: [false, t('plans.table.val.analyticsFull'), t('plans.table.val.analyticsFull'), t('plans.table.val.analyticsReports')] },
        { feature: t('plans.table.row.password'), values: [true, true, true, true] },
        { feature: t('plans.table.row.support'), values: [t('plans.table.val.supportCommunity'), t('plans.table.val.supportEmail'), t('plans.table.val.supportPriority'), t('plans.table.val.supportDedicated')] },
      ],
    },
  ]
}

/**
 * El resumen del panel de pago para un plan.
 *
 * Los textos viven aquí desde que el mismo panel también vende dominios: allí
 * no hay ciclo ni «/mes» que valga, así que quien abre el pago es quien sabe
 * cómo se llama lo que se cobra.
 */
function buildPlanSummary(
  plan: { name: string; description: string; monthlyPrice: number | null },
  features: string[],
  listPrice: number,
  annual: boolean,
  t: Translate,
): CheckoutSummary {
  const amount = annual ? Math.round(listPrice * BETA_DISCOUNT * 100) / 100 : listPrice
  return {
    title: plan.name,
    description: plan.description,
    features,
    amount,
    /* En anual se tacha el MENSUAL, como en la tarjeta: el pago tiene que
       contar la misma comparación que se vio al elegir. */
    listAmount: annual ? (plan.monthlyPrice ?? listPrice) : listPrice,
    unit: t('plans.perMonth'),
    billed: annual ? '' : t('plans.checkout.billedMonthly'),
    /* Sin precio a la izquierda: lo dice Stripe a la derecha, con el importe de
       hoy y el de después. Aquí sólo queda de qué es el descuento. */
    hidePrice: true,
    ...(annual ? {
      badge: t('plans.checkout.betaBadge', { percent: BETA_OFF_PERCENT }),
      note: t('plans.checkout.betaNote'),
    } : {}),
    backLabel: t('plans.checkout.back'),
    /* Partido en trozos porque lleva dos enlaces dentro: una sola clave con
       marcado no la puede traducir nadie sin escribir HTML. */
    legal: (
      <>
        {t('plans.checkout.legal1')}
        <a href="/terminos" target="_blank" rel="noreferrer">{t('plans.checkout.terms')}</a>
        {t('plans.checkout.legal2')}
        <a href="/privacidad" target="_blank" rel="noreferrer">{t('plans.checkout.privacy')}</a>
        {t('plans.checkout.legal3')}
      </>
    ),
  }
}

/**
 * Las preguntas que quedan después de comparar, contestadas con lo que hace el
 * código y no con lo que suena bien: cada respuesta sale de algo que el
 * servidor aplica —la cancelación al final del periodo, el `allowsWrite` de
 * `@webbuilder/plans`, el cupón sólo en anual, `PLATFORM_FEE_PERCENT`—. Las
 * cifras se interpolan de las mismas constantes que pintan la página, para que
 * el FAQ no pueda prometer otro descuento ni otra comisión.
 *
 * `<details>` y no un acordeón propio: abre y cierra con teclado y lector de
 * pantalla sin una línea de JavaScript.
 */
function PlansFaq({ saving }: { saving: number }) {
  const { t } = useI18n()
  const items = [
    { q: t('plans.faq.q.switch'), a: t('plans.faq.a.switch') },
    { q: t('plans.faq.q.downgrade'), a: t('plans.faq.a.downgrade') },
    { q: t('plans.faq.q.account'), a: t('plans.faq.a.account') },
    { q: t('plans.faq.q.beta'), a: t('plans.faq.a.beta', { beta: BETA_OFF_PERCENT, months: BETA_MONTHS, percent: saving }) },
    { q: t('plans.faq.q.domain'), a: t('plans.faq.a.domain') },
    {
      q: t('plans.faq.q.fee'),
      a: t('plans.faq.a.fee', {
        free: PLATFORM_FEE_PERCENT.free,
        pro: PLATFORM_FEE_PERCENT.pro,
        business: PLATFORM_FEE_PERCENT.business,
      }),
    },
    { q: t('plans.faq.q.payment'), a: t('plans.faq.a.payment') },
  ]
  return (
    <section className="dash-plans-faq" aria-labelledby="dash-plans-faq-title">
      <div className="dash-plans-faq-head">
        <h2 id="dash-plans-faq-title">{t('plans.faq.title')}</h2>
        <p>{t('plans.faq.lead')}</p>
      </div>
      <div className="dash-plans-faq-list">
        {items.map((item) => (
          <details key={item.q}>
            <summary>
              <span>{item.q}</span>
              <IconPlus size={16} aria-hidden="true" />
            </summary>
            <p>{item.a}</p>
          </details>
        ))}
      </div>
    </section>
  )
}

export function ProjectPlans({ token, onOpenBilling }: {
  token: string
  /**
   * A Facturación, que es donde se administra la membresía.
   *
   * «Gestionar suscripción» llevaba directo al portal de Stripe: una pantalla
   * de otra marca, sin el contexto de lo que se paga, para algo que Facturación
   * ya enseña con su renovación, su cancelación y su enlace al portal. Desde
   * allí se elige si hace falta Stripe.
   */
  onOpenBilling: () => void
}) {
  const { t } = useI18n()
  /* Se arman una vez por dibujado y no en el módulo: llevan texto, y el texto
     depende del idioma que esté puesto ahora mismo. */
  const plans = sitePlans(t)
  const annualSaving = annualSavingPercent(plans)
  /* Las siete filas fijas de cada tarjeta —cabecera, nombre, descripción,
     precio, condiciones, el título de la lista y el botón— más una por
     ventaja.
     CSS no suma dentro de `span`, así que el total llega ya calculado. */
  const featureRows = plans[0]?.features.length ?? 0
  const comparison = siteComparison(t)
  const [cycle, setCycle] = useState<BillingCycle>('annual')
  const cycleTabsRef = useRef<HTMLDivElement | null>(null)
  useTabThumb(cycleTabsRef, cycle)
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [currentPlan, setCurrentPlan] = useState<BillingPlan>(cachedBillingPlan)
  /* Hasta cuándo sigue vigente lo pagado. Lo dice el servidor y no se calcula
     aquí: es la fecha que el diálogo promete, y una fecha inventada en el
     cliente es una promesa que puede no cumplirse. */
  const [renewsAt, setRenewsAt] = useState<string | null>(null)
  // Set once from the ?checkout=success return, so the confirmation shows even
  // after we strip the query param from the URL.
  // The plan travels with the secret so the summary shows what was actually
  // chosen, instead of re-deriving it and risking a mismatch with the session.
  const [checkout, setCheckout] = useState<{ clientSecret: string; summary: CheckoutSummary } | null>(null)
  const [memberDialog, setMemberDialog] = useState(false)
  const [justPaid, setJustPaid] = useState(() => new URLSearchParams(window.location.search).get('checkout') === 'success')

  /**
   * Leaves for the dashboard home rather than back to the plans list — nobody
   * finishes paying to look at the pricing table again.
   *
   * A real navigation, not a state change: it reloads the app, so every screen
   * reads the new plan from the server instead of trusting whatever the client
   * happened to have cached before the payment.
   */
  const goToDashboard = () => {
    window.location.href = '/'
  }

  const currentPlanName = PLAN_NAME[currentPlan]
  /* La recomendada es siempre la siguiente a la que se tiene: Pro para quien
     está en Starter, Business para quien está en Pro, y así. Escrita a fuego en
     una tarjeta, le recomendaba Business a quien ya lo tenía y le enseñaba la
     misma cinta a todos. En el último plan no hay siguiente, así que no hay
     cinta. */
  const currentIndex = plans.findIndex((plan) => plan.name === currentPlanName)
  const recommendedName = currentIndex >= 0 ? plans[currentIndex + 1]?.name : undefined

  useEffect(() => {
    let alive = true
    refreshBilling(token)
      .then((b) => { if (alive) { setCurrentPlan(b.plan); setRenewsAt(b.currentPeriodEnd) } })
      .catch(() => {})
    return () => { alive = false }
  }, [token])

  /**
   * Bajar al plan gratuito ES cancelar la membresía.
   *
   * Su tarjeta no llevaba a ninguna parte: sin `planId` no hay nada que
   * contratar, así que el botón salía apagado. Pero quien mira la comparativa y
   * decide que le sobra el plan de pago está eligiendo bajarse, y hacerle
   * buscar el botón de cancelar en Facturación es esconder la salida.
   *
   * Se cancela al final del periodo ya pagado —no se corta hoy— porque ya está
   * pagado. Eso lo dice el diálogo antes de confirmar.
   */
  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const downgradeToFree = async () => {
    setCancelling(true)
    setError(null)
    try {
      await cancelMembership(token)
      /* Se relee en vez de darlo por hecho: la fecha hasta la que sigue vigente
         la decide el servidor, y el plan sigue siendo el de pago hasta entonces. */
      const billing = await refreshBilling(token)
      setCurrentPlan(billing.plan)
      setRenewsAt(billing.currentPeriodEnd)
      setCancelOpen(false)
    } catch (failure) {
      setError(errorMessage(failure))
    } finally {
      setCancelling(false)
    }
  }

  const handleChoose = async (plan: SitePlan) => {
    const planId = PLAN_ID[plan.name]
    if (!planId || loadingPlan) return
    setError(null)
    setLoadingPlan(plan.name)
    try {
      const { clientSecret } = await startCheckout(token, planId, cycle === 'annual' ? 'yearly' : 'monthly')
      const listPrice = (cycle === 'annual' ? plan.annualPrice : plan.monthlyPrice) ?? 0
      // Lo que se cobra mensual no lleva dominio: el resumen del pago enumera lo
      // que se contrata, y dejar ahí una ventaja que no entra sería prometerla
      // en la pantalla donde se acepta el cargo.
      const features = plan.features
        .filter((feature) => !feature.excluded)
        .filter((feature) => !feature.annualOnly || cycle === 'annual')
        .map((feature) => feature.label)
      setCheckout({
        clientSecret,
        summary: buildPlanSummary(plan, features, listPrice, cycle === 'annual', t),
      })
    } catch (failure) {
      const code = serverErrorCode(failure)
      if (code === 'billing.subscriptionExists') {
        /* Ya es miembro: un aviso con la salida a Facturación, no una línea roja.
           Se relee el plan por si la pantalla iba atrasada. */
        setMemberDialog(true)
        refreshBilling(token)
          .then((b) => { setCurrentPlan(b.plan); setRenewsAt(b.currentPeriodEnd) })
          .catch(() => {})
      } else if (code === 'billing.paymentInProgress') {
        /* El pago ya se hizo o está en marcha: la pantalla de gracias espera al
           webhook y dice cuándo queda activo. */
        setJustPaid(true)
      } else {
        /* Lo que diga el servidor: el fallo suele ser un precio o un cupón creado
           en el otro modo de Stripe, y eso no se arregla reintentando. */
        setError(errorMessage(failure))
      }
    } finally {
      // Cleared either way: the button stays disabled forever otherwise, since
      // the panel — not a redirect — is what follows a success now.
      setLoadingPlan(null)
    }
  }

  /**
   * Lo que cuesta un plan en el ciclo elegido. La beta sólo va en anual: en
   * mensual la cifra grande es la de lista y no se enseña ninguna otra. Lo usan
   * las tarjetas y la cabecera de la tabla.
   */
  const pricing = (plan: SitePlan) => {
    const price = cycle === 'annual' ? plan.annualPrice : plan.monthlyPrice
    const betaApplies = cycle === 'annual' && price !== null && price > 0
    const betaPrice = betaApplies && price !== null ? price * BETA_DISCOUNT : price
    return { price, betaApplies, betaPrice }
  }

  /**
   * El botón de un plan, escrito una vez: lo llevan la tarjeta y la cabecera
   * de la comparativa, y dos copias acabarían diciendo cosas distintas del
   * mismo plan. La del gratuito es la única que no contrata nada: baja de
   * plan, que es cancelar.
   */
  const planButton = (plan: SitePlan, compact = false) => {
    const isCurrent = plan.name === currentPlanName
    const planId = PLAN_ID[plan.name]
    /* Sólo desde un plan de pago: en el gratuito su propia tarjeta dice
       «Plan actual» y no hay nada que cancelar. */
    const canDowngrade = plan.name === 'Starter' && currentPlan !== 'free'
    const arrow = compact ? null : <IconArrowRight size={15} />
    return (
      <button
        className={`${!isCurrent && !plan.comingSoon ? 'dash-primary-btn' : 'dash-secondary-btn'} dash-plan-cta`}
        type="button"
        disabled={(!planId && !canDowngrade) || plan.comingSoon || loadingPlan !== null || cancelling}
        onClick={() => void (canDowngrade
          ? setCancelOpen(true)
          : isCurrent && planId ? onOpenBilling() : handleChoose(plan))}
      >
        {canDowngrade
          ? <>{t('plans.toFree')}{arrow}</>
          : isCurrent && planId
            ? <>{t('plans.manageSubscription')}{arrow}</>
            : isCurrent
              ? t('plans.currentPlan')
              : loadingPlan === plan.name
                ? <><IconLoader2 size={15} className="dash-spin" /> {t('plans.redirecting')}</>
                : <>{plan.cta}{planId && !plan.comingSoon ? arrow : null}</>}
      </button>
    )
  }

  return (
    /* El mismo armazón que cualquier otra pantalla del workspace: encabezado de
       página normal, banner de oferta, fila de periodo y contenido. Antes esto
       abría con un héroe de degradados, círculos decorativos y un título del
       doble de tamaño que el de cualquier otra sección; la pantalla se leía
       como de otra aplicación. */
    <div className="dash-projects-view dash-pages-view dash-project-plans-page">
      <DashboardPageHeader
        title={t('plans.title')}
        subtitle={t('plans.subtitle')}
      />

      {/* La oferta toma el azul y la silueta de la mascota: la promoción sigue
          siendo parte de la marca sin convertir el personaje en una ilustración
          que compita con los precios. Más baja que antes: es el contexto de
          los precios, no el titular de la página. */}
      <section className="dash-plans-banner" aria-label={t('plans.bannerAria')}>
        <div className="dash-plans-banner-copy">
          <span className="bento-eyebrow">{t('plans.betaEyebrow')}</span>
          {/* El 40% por delante y con un ejemplo en dólares: dos porcentajes
              seguidos —el de la Beta y el ahorro contra pagar mes a mes— se
              leían como una contradicción. Aquí se ve la escalera entera con
              los precios de Pro. */}
          <h2>{t('plans.betaTitle', { percent: BETA_OFF_PERCENT })}</h2>
          <p className="dash-plans-banner-body-desktop">{t('plans.betaBody', {
            percent: BETA_OFF_PERCENT,
            months: BETA_MONTHS,
            monthly: formatPrice(PLAN_PRICES.pro.monthly),
            annual: formatPrice(PLAN_PRICES.pro.annual),
            now: formatPrice(PLAN_PRICES.pro.annual * BETA_DISCOUNT),
            total: formatPrice(PLAN_PRICES.pro.annual * BETA_DISCOUNT * 12),
          })}</p>
          <details className="dash-plans-banner-body-mobile">
            <summary>{t('plans.betaDetails')}</summary>
            <p>{t('plans.betaBody', {
              percent: BETA_OFF_PERCENT,
              months: BETA_MONTHS,
              monthly: formatPrice(PLAN_PRICES.pro.monthly),
              annual: formatPrice(PLAN_PRICES.pro.annual),
              now: formatPrice(PLAN_PRICES.pro.annual * BETA_DISCOUNT),
              total: formatPrice(PLAN_PRICES.pro.annual * BETA_DISCOUNT * 12),
            })}</p>
          </details>
        </div>
        <MascotOutlinePattern />
      </section>

      {/* El periodo, centrado encima de las tarjetas: es el mando que cambia
          los cuatro precios a la vez, y ahí es donde lo ponen Notion, Figma y
          Webflow. Antes era una fila de formulario con bordes arriba y abajo,
          que se leía como un ajuste más y no como parte de los precios. */}
      <div className="dash-plans-cycle">
        {/* El ahorro va dentro del propio botón «Anual» y no en una etiqueta
            aparte: suelto al lado del control se leía como un dato más de la
            fila, no como la razón para pulsar ese botón. */}
        <div
          className="dash-booking-service-tabs dash-billing-cycle"
          ref={cycleTabsRef}
          role="tablist"
          aria-label={t('plans.cycle')}
          onKeyDown={(event) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
            const tabs = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'))
            const current = tabs.indexOf(document.activeElement as HTMLButtonElement)
            if (current < 0) return
            event.preventDefault()
            const direction = event.key === 'ArrowRight' ? 1 : -1
            const next = tabs[(current + direction + tabs.length) % tabs.length]
            next?.focus()
            next?.click()
          }}
        >
          <span className="dash-booking-service-tabs-thumb" aria-hidden="true" />
          <button
            type="button"
            role="tab"
            aria-selected={cycle === 'monthly'}
            tabIndex={cycle === 'monthly' ? 0 : -1}
            className={cycle === 'monthly' ? 'is-active' : ''}
            onClick={() => setCycle('monthly')}
          >
            {t('plans.monthly')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={cycle === 'annual'}
            tabIndex={cycle === 'annual' ? 0 : -1}
            className={cycle === 'annual' ? 'is-active' : ''}
            onClick={() => setCycle('annual')}
          >
            {t('plans.annual')}
            <span className="dash-billing-cycle-saving">{t('plans.annualSaving', { percent: annualSaving })}</span>
          </button>
        </div>
        <p>{t('plans.cycleBody', { months: BETA_MONTHS })}</p>
      </div>

      <div
        className="dash-pricing-grid"
        /* Cuántas filas de ventajas hay: la cuadrícula necesita saberlo para
           reservar una fila compartida por cada una. */
        style={{
          '--plan-feature-rows': featureRows,
          '--plan-list-rows': featureRows + 1,
          '--plan-card-rows': featureRows + 7,
        } as CSSProperties}
      >
        {plans.map((plan) => {
          const { price, betaApplies, betaPrice } = pricing(plan)
          const isCurrent = plan.name === currentPlanName
          const classes = [
            'dash-pricing-card',
            isCurrent ? 'is-current' : '',
            plan.comingSoon ? 'is-soon' : '',
            plan.name === recommendedName ? 'is-recommended' : '',
          ].filter(Boolean).join(' ')
          return (
            <article className={classes} key={plan.name}>
              {/* La recomendación es una cinta en el canto superior, fuera del
                  flujo (absoluta), así que no cuenta como una fila más de la
                  cuadrícula compartida. */}
              {plan.name === recommendedName ? <span className="dash-pricing-card-ribbon">{t('plans.recommended')}</span> : null}
              <div className="dash-pricing-card-topline">
                <span>{plan.eyebrow}</span>
                {plan.comingSoon ? <em className="is-soon">{t('plans.soon')}</em> : isCurrent ? <em className="is-current">{t('plans.current')}</em> : null}
              </div>
              <h3>{plan.name}</h3>
              <p className="dash-pricing-description">{plan.description}</p>
              {/* El precio en dos pisos: arriba lo que se tacha y la etiqueta
                  del descuento, abajo la cifra grande. El bloque se apoya en su
                  base, así que las cifras grandes de las cuatro tarjetas quedan
                  a la misma altura tengan o no piso de arriba. */}
              <div className="dash-pricing-price">
                {price === null ? (
                  <div className="dash-pricing-price-now"><strong>{t('plans.custom')}</strong></div>
                ) : (
                  <>
                    {betaApplies ? (
                      <div className="dash-pricing-price-was">
                        {/* Tachado el precio ANUAL por mes: «$17 → $10.20» es
                            exactamente el descuento de la etiqueta (40%). Tachar
                            el mensual ($21) enseñaba un 51% junto a una
                            etiqueta que decía 40%, y la gente no sabía cuál
                            creer. El ahorro contra pagar mes a mes lo cuenta el
                            conmutador de Anual. */}
                        <s>${formatPrice(price)}</s>
                        <em className="dash-pricing-beta-tag">{t('plans.vsMonthly', { percent: BETA_OFF_PERCENT })}</em>
                      </div>
                    ) : null}
                    <div className="dash-pricing-price-now">
                      <strong>${formatPrice(betaPrice ?? 0)}</strong>
                      <span>{t('plans.perMonth')}</span>
                    </div>
                  </>
                )}
              </div>
              {/* Las condiciones en una caja suave: lo que se cobra hoy y en qué
                  se convierte. El gratuito no tiene condiciones que contar, así
                  que dice lo único que importa de él. */}
              {price !== null && price > 0 ? (
                <dl className="dash-pricing-terms">
                  {/* Con la Beta, la cuenta a la vista como un recibo: el precio
                      normal, lo que se descuenta y lo que se paga. */}
                  {betaApplies ? (
                    <>
                      <div>
                        <dt>{t('plans.regularAnnual')}</dt>
                        <dd>{t('plans.pricePerYear', { price: formatPrice((price ?? 0) * 12) })}</dd>
                      </div>
                      <div>
                        <dt>{t('plans.betaDiscountRow', { percent: BETA_OFF_PERCENT })}</dt>
                        <dd className="is-discount">−${formatPrice((price ?? 0) * 12 - (betaPrice ?? 0) * 12)}</dd>
                      </div>
                    </>
                  ) : null}
                  <div className={betaApplies ? 'is-total' : undefined}>
                    <dt>{t(plan.comingSoon ? 'plans.onLaunch' : 'plans.todayYouPay')}</dt>
                    <dd>${formatPrice(cycle === 'annual' ? (betaPrice ?? 0) * 12 : (betaPrice ?? 0))}</dd>
                  </div>
                  {/* En el mensual no se enseña lo que costaría pagando al año:
                      con el conmutador en «mensual», un segundo precio más bajo
                      en la misma tarjeta hacía dudar de cuál se cobra. El ahorro
                      anual ya lo cuenta el propio conmutador. */}
                  {betaApplies ? (
                    <div className="is-note">
                      {/* El año completo y no «$17/mes»: dicho por mes se leía
                          como que a los doce meses pasaba a cobro mensual. Sigue
                          siendo anual; lo que se acaba es la oferta Beta. */}
                      <dt>{t('plans.renewNote')}</dt>
                    </div>
                  ) : null}
                </dl>
              ) : <p className="dash-pricing-terms is-free">{t('plans.freeForever')}</p>}
              <div className="dash-pricing-includes-block">
                <strong className="dash-pricing-includes">{t('plans.includes')}</strong>
                {/* Una fila por ventaja, las mismas en las cuatro tarjetas: la
                    cuadrícula las pone a la misma altura (ver «Filas de
                    ventajas» en el CSS). Lo que el plan no trae sale con ✗ y en
                    gris; lo que sólo trae el plan anual, igual cuando se mira el
                    mensual, con la nota de por qué. */}
                <ul className="dash-pricing-features">
                  {plan.features.map(({ label, annualOnly, excluded }, index) => {
                    const annualMissing = annualOnly === true && cycle === 'monthly'
                    const off = excluded === true || annualMissing
                    return (
                      <li className={off ? 'is-excluded' : ''} key={index}>
                        <span className="dash-pricing-feature-mark" aria-hidden="true">
                          {off ? <IconX size={11} /> : <IconCheck size={12} />}
                        </span>
                        <span>
                          {off ? <span className="dash-sr-only">{t('plans.table.notIncluded')}: </span> : null}
                          {label}
                          {annualMissing ? <em>{t('plans.annualOnly')}</em> : null}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </div>
              {/* El botón, después de las ventajas: con siete filas la lista es
                  corta, y así se decide habiendo leído qué trae el plan. Todas
                  las tarjetas lo tienen en la misma fila compartida. La del
                  gratuito es la única cuyo botón no contrata nada: baja de
                  plan, que es cancelar. */}
              {planButton(plan)}
            </article>
          )
        })}
      </div>

      {error ? <p className="dash-pricing-error" role="alert">{error}</p> : null}

      {/* Las tres dudas que frenan a quien está a punto de pagar, contestadas
          donde surgen. Las tres son verdad en el código: cobra Stripe, se
          cancela desde Facturación y nada se borra al bajar de plan (ver
          `allowsWrite` en `@webbuilder/plans`). */}
      <ul className="dash-plans-trust" aria-label={t('plans.trust.aria')}>
        <li><IconShieldCheck size={18} /><span>{t('plans.trust.secure')}</span></li>
        <li><IconRefresh size={18} /><span>{t('plans.trust.cancel')}</span></li>
        <li><IconLock size={18} /><span>{t('plans.trust.keep')}</span></li>
      </ul>
      <p className="dash-pricing-fineprint">{t('plans.fineprint')}</p>

      <PlanComparisonTable
        title={t('plans.allFeatures')}
        columns={plans.map((plan) => {
          const isCurrent = plan.name === currentPlanName
          return {
            name: plan.name,
            action: isCurrent ? t('plans.current') : '',
            cta: planButton(plan, true),
            ...(isCurrent ? { current: true } : {}),
            ...(plan.name === recommendedName ? { highlight: true } : {}),
          }
        })}
        sections={comparison}
      />

      <PlansFaq saving={annualSaving} />

      {justPaid ? <CheckoutSuccess token={token} onDone={goToDashboard} /> : null}
      {memberDialog ? <MembershipExistsDialog onBilling={() => { setMemberDialog(false); onOpenBilling() }} onClose={() => setMemberDialog(false)} /> : null}
      {checkout ? (
        <EmbeddedCheckoutPanel
          clientSecret={checkout.clientSecret}
          summary={checkout.summary}
          // Closing is not a cancellation: the session stays open in Stripe, so
          // reopening the plan starts a fresh one rather than resuming a stale
          // secret that may already have been completed in another tab.
          onClose={() => {
            setCheckout(null)
            /* Cerrar el formulario cierra también el pago: si no, quedaba abierto
               hasta caducar y volver a intentarlo chocaba con él. Sin esperar la
               respuesta; si falla, abrir otro lo cierra igual. */
            void cancelCheckout(token).catch(() => {})
          }}
          onComplete={() => { setCheckout(null); setJustPaid(true) }}
        />
      ) : null}

      {/* Lo que de verdad pasa al bajar de plan, dicho antes de confirmar.
          Lo importante no es «vas a cancelar» sino las dos cosas que la gente
          teme y que no ocurren: el sitio no se cae y no se borra nada. Sin eso,
          quien duda no pulsa —y quien pulsa se lleva un susto al día siguiente
          buscando su sitio. */}
      {cancelOpen ? (
        <ConfirmDialog
          title={t('plans.confirmFree')}
          wide
          /* El mismo resumen que en Facturación, no una versión resumida:
             cancelar desde aquí y cancelar desde allá tienen que contar lo
             mismo, y cuando cada pantalla escribía su propio aviso ninguna de
             las dos decía lo que decía la otra. */
          message={<CancelMembershipSummary token={token} plan={currentPlan} until={untilLabel(renewsAt, t)} annual={cycle === 'annual'} />}
          confirmLabel={t('plans.confirmFreeYes')}
          confirmingLabel={t('plans.cancelling')}
          cancelLabel={t('plans.stayOn', { plan: currentPlanName ?? '' })}
          tone="danger"
          onConfirm={() => void downgradeToFree()}
          onCancel={() => setCancelOpen(false)}
        />
      ) : null}
    </div>
  )
}
